import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2 } from 'lucide-react';

const METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'complimentary', label: 'Complimentary' },
];

export default function MarkPaidDialog({ open, onOpenChange, booking, onConfirm }) {
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm({
        payment_method: method,
        payment_reference: reference.trim() || undefined,
      });
      // reset for next open
      setMethod('cash');
      setReference('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mark-paid-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            Mark Booking as Paid
          </DialogTitle>
          <DialogDescription className="sr-only">
            Confirm payment received for this held booking and select the payment method.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {booking && (
            <p className="text-sm text-muted-foreground">
              Booking <strong>#{(booking.id || '').slice(0, 8).toUpperCase()}</strong> —
              ${(booking.final_total ?? 0).toFixed(2)}
            </p>
          )}

          <div>
            <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">
              Payment Method *
            </Label>
            <div className="flex flex-wrap gap-2">
              {METHODS.map((m) => {
                const active = method === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setMethod(m.value)}
                    data-testid={`mark-paid-method-${m.value}`}
                    className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${
                      active
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
              Reference (optional)
            </Label>
            <Input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. transfer slip / cheque #"
              className="h-11 rounded-xl"
              data-testid="mark-paid-reference"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              className="flex-1 btn-volt"
              disabled={submitting}
              data-testid="mark-paid-confirm"
            >
              {submitting ? 'Saving...' : 'Confirm Paid'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
